import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Keyboard, Platform, StatusBar, StyleSheet, View } from 'react-native';

import FastImage from 'react-native-fast-image';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';

import { NetInfoStateType } from '@react-native-community/netinfo';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';

import { MOBILE_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE } from 'config/constants.ts';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import {
    selectAppState,
    selectLayoutConfig,
    selectScreenReaderEnabled,
} from 'store/config/selectors';
import { setModalPlayerFullscreen, setPlayer } from 'store/player/actions';
import { StreamTypes } from 'store/player/actions.types';
import {
    selectModalPlayerFullscreen,
    selectPlayerChannel,
    selectPlayerConfig,
} from 'store/player/selectors';
import { selectChannels } from 'store/programs/selectors';
import { startSentryTransaction } from 'store/sentry/actions';
import { SENTRY_TRANSACTIONS } from 'store/sentry/constants';
import { selectActiveUserProfile } from 'store/user/selectors';

import { VideoItem } from 'services/data-manager/types';
import { RetryController } from 'services/opf-retry-mechanism';

import { useCast } from 'utils/cast/useCast';
import { COLOR } from 'utils/colors';
import debugLog from 'utils/debugLog';
import isFunction from 'utils/function';
import useRecordings from 'utils/hooks/useRecordings';
import {
    lockToDefaultOrientation,
    useDeviceOrientation,
} from 'utils/orientation';
import { isRecordingItem } from 'utils/recordings';
import { isWeb } from 'utils/responsiveness';

import { DetailsRequestUrlType } from 'screens/Detail/types';
import useAudioAndSubtitlesDialog from 'screens/PlayerModal/hooks/useAudioAndSubtitlesDialog';
import useModalAccessibility from 'screens/PlayerModal/hooks/useModalAccessibility.ts';
import useOnPlayerClose from 'screens/PlayerModal/hooks/useOnPlayerClose';

import ConnectionIndicatorAlert from 'components/ConnectionIndicatorAlert';
import useConnectionIndicator from 'components/ConnectionIndicatorAlert/hooks';
import {
    AspectRatio,
    ImageContentType,
    ImageSize,
    ImageType,
} from 'components/NagraImage/types';
import { getImageUrl } from 'components/NagraImage/utils';
import NoInternetAlert from 'components/NoInternetAlert';
import Notification from 'components/Notification';
import Player from 'components/Player';
import PlayerOverlay from 'components/PlayerOverlay';
import BingeWatchingOverlay from 'components/PlayerOverlay/BingeWatchingOverlay';
import ContentRatingOverlay from 'components/PlayerOverlay/ContentRatingOverlay';
import RecordingsNotificationsAndOverlay from 'components/RecordingsNotificationsAndOverlays';
import useRecordingsNotificationsAndOverlays from 'components/RecordingsNotificationsAndOverlays/hooks/useRecordingsNotificationsAndOverlays';
import useRecordingActionsDialog from 'components/SelectRecordingActions/hooks/useRecordingActionsDialog';

import GenericPinDialog from '../../components/GenericPinDialog';
import useCloseModalPlayer from './hooks/useCloseModalPlayer';
import usePlayerBackHandler from './hooks/usePlayerBackHandler';
import usePlayerSharedBehaviour from './hooks/usePlayerSharedBehaviour';
import useVideoEnd from './hooks/useVideoEnd';
import styles from './styles';
import { RecordingType, SwitchPlayerSource } from './types';
import { enterWebFullscreen, exitWebFullscreen } from './utils';

const PLAYER_MODAL_ID = 'player-modal';

function PlayerModal() {
    const dispatch = useDispatch();

    const appState = useSelector(selectAppState);
    const fullscreen = useSelector(selectModalPlayerFullscreen);
    const selectedProfileId = useSelector(selectActiveUserProfile);
    const { primary = COLOR.primary } = useSelector(selectLayoutConfig);
    const channels = useSelector(selectChannels);
    const screenReaderEnabled = useSelector(selectScreenReaderEnabled);

    const navigation =
        useNavigation<
            StackNavigationProp<RouteNavigationParams, SCREEN.DETAILS>
        >();
    const orientation = useDeviceOrientation();

    const onModalClosedCallback = useRef<() => void | undefined>(undefined);
    const shouldOpenDetailsInWeb = useRef(false);

    const [isRecordingDialogVisible, setIsRecordingDialogVisible] =
        useState<boolean>(false);

    const handleSwitchPlayerSource = useCallback<SwitchPlayerSource>(
        (videoItem?: VideoItem, playbackType?: StreamTypes) => {
            dispatch(
                setPlayer({
                    videoItem,
                    playbackType,
                }),
            );
        },
        [dispatch],
    );

    const { visible, videoItem, playbackType, railId, templateId } =
        useSelector(selectPlayerConfig);

    const channel = useSelector(selectPlayerChannel);

    const onCloseModalPlayer = useCloseModalPlayer();

    const handleCloseModalInWeb = useCallback(() => {
        window.history.back();
    }, []);

    const handleClosePlayer = useCallback(() => {
        if (isWeb) {
            handleCloseModalInWeb();
        } else {
            onCloseModalPlayer();
        }
    }, [handleCloseModalInWeb, onCloseModalPlayer]);

    const {
        connectionType,
        prevConnectionTypeRef,
        shouldWarnOnMobileData,
        selectedConnectionType,
    } = useConnectionIndicator();

    const {
        recordingItem,
        recordingIsProtected,
        // single recording data
        isRecordingLoading,
        // series recording data
        seriesId,
        seriesHasProtectedRecording,
        // handlers
        handleCancelRecording,
        handleUnprotectRecording,
        handleDeleteRecording,
        handleDeleteSeriesRecording,
        handleCancelSeriesRecording,
        // dialogs and notifications
        recordingsNotification,
        showQuotaDialog,
        showCancelEpisodeDialog,
        showCancelSeriesDialog,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,
        resetNotificationCallback,
        hideCancelEpisodeDialogCallback,
        hideCancelSeriesDialogCallback,
        hideDeleteRecordingDialogCallback,
        hideDeleteSeriesDialogCallback,
        hideQuotaDialogCallback,
        // buttons
        recordingsButtonsList,
        onManageRecordingPress,
        // permissions
        hasGlobalRecordingPermission,
    } = useRecordings({
        item: videoItem?.originalProgram,
        type: RecordingType.PLAYER,
        playerVisible: visible,
    });

    const videoItemRef = useRef<VideoItem | undefined>(undefined);
    const playbackTypeRef = useRef<StreamTypes | undefined>(undefined);

    useEffect(() => {
        if (videoItem) {
            videoItemRef.current = videoItem;
        }

        if (playbackType) {
            playbackTypeRef.current = playbackType;
        }
    }, [playbackType, videoItem]);

    const handleRestoreLiveStream = useCallback(() => {
        setTimeout(
            () =>
                handleSwitchPlayerSource(
                    videoItemRef.current,
                    playbackTypeRef.current,
                ),
            0,
        );
    }, [handleSwitchPlayerSource]);

    const {
        subtitlesDialogVisible,
        subtitleButtonPosition,
        onSubtitlesButtonPress,
        onCloseSubtitlesDialog,
        onSubtitlesModalClosed,
    } = useAudioAndSubtitlesDialog();

    const {
        jumpToLiveAllowed,
        onVideoLoaded,
        onPlayerReady,
        handleSeeked,
        playerRef,
        isCatchup,
        isNpvr,
        isVod,
        errorExists,
        skipBackwardDisabled,
        skipForwardDisabled,
        muted,
        subtitlesFontSize,
        videoSource,
        showPinDialog,
        isSOTSPauseDisabled,
        timeshiftNotification,
        disabledNotification,
        onResetDisabledNotification,
        seeking,
        onPlayerProgress,
        onStatisticsUpdate,
        onSlidingValueChanged,
        onSlidingStart,
        onSlidingCompleted,
        onSlidingCompleteSilently,
        onPlayPausePress,
        handleStopPlayer,
        onParentalCheckDismiss,
        onParentalControlChecked,
        handlePlayerError,
        handlePlaying,
        handleSkipForward,
        handleSkipBackward,
        handleJumpToLive,
        handleStartover,
        playerError,
        videoDuration,
        playerProgress,
        livePosition,
        playerReady,
        playerPaused,
        isLive,
        isStartover,
        timeshiftModeOn,
        timeshiftPosition,
        timeshiftDuration,
        startoverButtonVisible,
        startoverAllowed,
        pauseAccessLevel,
        rewindAccessLevel,
        fastforwardAccessLevel,
        onResetTimeshiftNotification,
        onStopPlayback,
        playerTracks,
        selectedAudio,
        selectedSubtitles,
        onTracksChanged,
        onSelectAudio,
        onSelectSubtitles,
        timeshiftIndicatorEnabled,
        showContentRatingOverlay,
        handleSetMuted,
        zapping,
        frameFitting,
        onResetPlayerError,
        volume,
        handleChangeVolume,
        playerWaiting,
        handleWaiting,
        bingewatchingCountdown,
        isBingeWatchingVisible,
        nextEpisode,
        onPlayNextEpisode,
        onCloseBingeWatching,
        bingeTransitionInProgressRef,
    } = usePlayerSharedBehaviour({
        appState,
        channel,
        videoItem,
        fullscreen,
        visible,
        playbackType,
        railId,
        templateId,
        profileId: selectedProfileId,
        onSwitchPlayerSource: handleSwitchPlayerSource,
        recordingId: recordingItem?.id,
        shouldWarnOnMobileData,
        handleRestoreLiveStream,
        subtitlesDialogVisible,
        recordingActionsDialogVisible: isRecordingDialogVisible,
    });

    useEffect(() => {
        if (
            !isWeb &&
            connectionType === NetInfoStateType.wifi &&
            prevConnectionTypeRef.current === NetInfoStateType.cellular &&
            selectedConnectionType === NetInfoStateType.wifi
        ) {
            setTimeout(
                () =>
                    handleSwitchPlayerSource(
                        videoItemRef.current,
                        playbackTypeRef.current,
                    ),
                0,
            );
        }
    }, [connectionType]);

    const { cast, isCastConnected } = useCast();

    const handleRedirectToDetails = useCallback(() => {
        if (videoItem) {
            handleStopPlayer();

            if (playbackType !== StreamTypes.vod) {
                let params: RouteNavigationParams[SCREEN.DETAILS] = {
                    templateId,
                    railId,
                    filterType: DetailsRequestUrlType.PROGRAM,
                    id: isRecordingItem(videoItem.originalProgram)
                        ? videoItem.originalProgram?.contentId
                        : videoItem.originalProgram?.id,
                    channelId: videoItem.channelId,
                };

                if (videoItem.seriesId) {
                    params = {
                        ...params,
                        seriesId: videoItem.seriesId,
                        season: videoItem.seasonNumber,
                        episode: videoItem.episodeNumber,
                    };
                }

                dispatch(
                    startSentryTransaction(
                        SENTRY_TRANSACTIONS.DetailPageRendering,
                    ),
                );
                navigation.navigate(SCREEN.DETAILS, params);
            }

            requestAnimationFrame(() => {
                onCloseModalPlayer();
                onResetPlayerError();
            });
        }
    }, [
        navigation,
        videoItem,
        templateId,
        railId,
        playbackType,
        handleCloseModalInWeb,
        handleStopPlayer,
        onCloseModalPlayer,
        onResetPlayerError,
    ]);

    const handleOpenDetails = useCallback(() => {
        if (isWeb) {
            shouldOpenDetailsInWeb.current = true;
            handleCloseModalInWeb();
        } else {
            handleRedirectToDetails();
        }
    }, [handleRedirectToDetails, handleCloseModalInWeb]);

    const {
        pinDialogProps,
        onManageRecordingPressCallback,
        handleShowPinDialog,
    } = useRecordingsNotificationsAndOverlays({
        handleOpenDetails,
        handleUnprotectRecording,
        onManageRecordingPress,
        handleDeleteRecording,
        handleDeleteSeriesRecording,
    });

    const recordingActionsDialogState = useRecordingActionsDialog({
        isRecordingLoading,
        recordingsButtonsList,
        hasGlobalRecordingPermission,
        onManageRecordingPressCallback,
    });

    const handleCloseModal = useCallback(() => {
        if (RetryController.vitalDialogVisible) {
            return;
        }

        if (subtitlesDialogVisible) {
            onCloseSubtitlesDialog();
        }

        if (showCancelSeriesDialog) {
            hideCancelSeriesDialogCallback();
        } else if (showCancelEpisodeDialog) {
            hideCancelEpisodeDialogCallback();
        } else if (showDeleteSeriesDialog) {
            hideDeleteSeriesDialogCallback();
        } else if (showDeleteRecordingDialog) {
            hideDeleteRecordingDialogCallback();
        } else if (showQuotaDialog) {
            hideQuotaDialogCallback();
        } else {
            if (zapping.channelsZapping) {
                zapping.handleStopZapping();
            }

            handleStopPlayer();

            requestAnimationFrame(() => {
                onCloseModalPlayer();
                onResetPlayerError();

                if (!isWeb) {
                    videoItemRef.current = undefined;
                    playbackTypeRef.current = undefined;
                }
            });
        }

        if (isWeb && shouldOpenDetailsInWeb.current) {
            handleRedirectToDetails();
        }

        Keyboard.dismiss();
    }, [
        zapping.channelsZapping,
        showCancelEpisodeDialog,
        showCancelSeriesDialog,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,
        showQuotaDialog,
        subtitlesDialogVisible,
        onCloseSubtitlesDialog,
        handleStopPlayer,
        onCloseModalPlayer,
        handleRedirectToDetails,
        onResetPlayerError,
        hideCancelEpisodeDialogCallback,
        hideCancelSeriesDialogCallback,
        hideDeleteRecordingDialogCallback,
        hideDeleteSeriesDialogCallback,
        hideQuotaDialogCallback,
    ]);

    const handleClosePinDialog = useCallback(() => {
        onParentalCheckDismiss();

        if (!zapping.zappingEnabled && isWeb) {
            handleCloseModal();
        }
    }, [handleCloseModal, onParentalCheckDismiss, zapping.zappingEnabled]);

    const onVideEnd = useVideoEnd({
        isStartover,
        onJumpToLive: handleJumpToLive,
    });

    const handleVideoEnd = useCallback(() => {
        if (isCatchup || isVod || isNpvr) {
            debugLog('isBingeWatchingVisible', isBingeWatchingVisible);
            debugLog(
                ' bingeTransitionInProgressRef.current',
                bingeTransitionInProgressRef.current,
            );
            // If binge watching overlay is active or a transition to the next episode
            // was already triggered, don't close the modal — let binge watching handle it
            if (
                isBingeWatchingVisible ||
                bingeTransitionInProgressRef.current
            ) {
                debugLog('handleVideoEnd', 'do not close');
            } else if (isWeb) {
                handleCloseModalInWeb();
            } else {
                handleCloseModal();
            }
        } else {
            onVideEnd();
        }
    }, [
        isCatchup,
        isVod,
        isNpvr,
        isBingeWatchingVisible,
        handleCloseModalInWeb,
        handleCloseModal,
        onVideEnd,
    ]);

    const handlePlayerClosed = useCallback(() => {
        onStopPlayback();

        if (isFunction(onModalClosedCallback.current)) {
            onModalClosedCallback.current();
            onModalClosedCallback.current = undefined;
        }

        if (isWeb) {
            shouldOpenDetailsInWeb.current = false;
        }
    }, [onStopPlayback]);

    const handleEnterWebFullscreen = useCallback(async () => {
        if (isWeb) {
            await enterWebFullscreen(PLAYER_MODAL_ID);
        }
    }, []);

    const handleExitWebFullscreen = useCallback(async () => {
        if (isWeb) {
            await exitWebFullscreen();
        }
    }, []);

    const handleFullscreenChange = useCallback(() => {
        dispatch(setModalPlayerFullscreen(!!document.fullscreenElement));
    }, []);

    useEffect(() => {
        setIsRecordingDialogVisible(
            recordingActionsDialogState.recordingActionsDialogVisible,
        );
    }, [recordingActionsDialogState.recordingActionsDialogVisible]);

    useEffect(() => {
        if (isWeb) {
            if (visible) {
                document.addEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            } else {
                document.removeEventListener(
                    'fullscreenchange',
                    handleFullscreenChange,
                );
            }
        }
    }, [visible]);

    useEffect(
        () => () => {
            onCloseSubtitlesDialog();
            handleStopPlayer();
            onCloseModalPlayer();
            handleSwitchPlayerSource();
            resetNotificationCallback();
        },
        [],
    );

    useEffect(() => {
        if (!visible) {
            // REAC-826: Close PIN dialog if it was opened in player,
            // and user closed player via browser back button
            pinDialogProps.handlePinDialogClose();
            handleSwitchPlayerSource();
            onParentalCheckDismiss(false);
            setTimeout(lockToDefaultOrientation, 100);
        }
    }, [visible, handleSwitchPlayerSource]);

    useEffect(() => {
        if (
            isCastConnected &&
            videoItem &&
            Platform.OS === 'android' &&
            playbackType
        ) {
            onCloseSubtitlesDialog();
            handleStopPlayer();
            onCloseModalPlayer();

            setTimeout(() => {
                cast(
                    videoItem,
                    playbackType,
                    true,
                    selectedSubtitles,
                    selectedAudio,
                    playerProgress,
                );
            }, 250);
        }
    }, [isCastConnected, videoItem, playbackType]);

    useEffect(() => {
        if (isWeb) {
            if (
                showDeleteRecordingDialog ||
                showDeleteSeriesDialog ||
                pinDialogProps.showPinDialog
            ) {
                window.addEventListener('popstate', handleCloseModal);
            }
        }
    }, [
        pinDialogProps.showPinDialog,
        showDeleteRecordingDialog,
        showDeleteSeriesDialog,
    ]);

    useEffect(() => {
        if (isWeb && visible) {
            window.history.pushState({}, '', null);
        }
    }, [visible]);

    useEffect(() => {
        if (isWeb && visible) {
            window.addEventListener('popstate', handleCloseModal);
        }

        return () => {
            if (isWeb) {
                window.removeEventListener('popstate', handleCloseModal);
            }
        };
    }, [visible, handleCloseModal]);

    useEffect(() => {
        // Preload channels logos for channels zapping overlay animation.
        if (!isWeb && channels.length) {
            FastImage.preload(
                channels.map(({ editorial: { id } }) => ({
                    uri: getImageUrl({
                        contentId: id,
                        contentType: ImageContentType.CHANNEL,
                        imageType: ImageType.LOGO,
                        aspect: AspectRatio.LANDSCAPE,
                        width: ImageSize.ZAPPING_OVERLAY_CHANNEL_LOGO.width,
                    }),
                })),
            );
        }
    }, [channels]);

    useOnPlayerClose({ visible, onPlayerClosed: handlePlayerClosed });
    usePlayerBackHandler({ visible, onHardwareBackPress: handleCloseModal });
    useModalAccessibility();

    if (!visible) {
        return null;
    }

    return (
        <View
            id={PLAYER_MODAL_ID}
            style={StyleSheet.absoluteFill}
            accessible={false}
            accessibilityViewIsModal
            role="dialog"
            aria-modal
        >
            <StatusBar hidden={fullscreen} translucent />
            <GestureHandlerRootView style={styles.container} accessible={false}>
                <View style={styles.safeArea} accessible={false}>
                    <Player
                        ref={playerRef}
                        source={videoSource}
                        style={
                            frameFitting.frameFittingOn && playerReady
                                ? styles.fitPlayer
                                : styles.player
                        }
                        muted={muted}
                        volume={volume}
                        statisticsUpdateInterval={30000}
                        webSubtitleFontSize={subtitlesFontSize}
                        mobileSubtitleFontSize={
                            MOBILE_SUBTITLES_FULLSCREEN_PLAYER_FONT_SIZE[
                                subtitlesFontSize
                            ]
                        }
                        onPlayerSourceLoaded={onVideoLoaded}
                        onReady={onPlayerReady}
                        onProgress={onPlayerProgress}
                        onSeeked={handleSeeked}
                        onEnd={handleVideoEnd}
                        onError={handlePlayerError}
                        onPlaying={handlePlaying}
                        onWaiting={handleWaiting}
                        onStatisticsUpdate={onStatisticsUpdate}
                        onTracksChanged={onTracksChanged}
                    />
                    <PlayerOverlay
                        screenReaderEnabled={screenReaderEnabled}
                        fullscreen={fullscreen}
                        isSOTSPauseDisabled={isSOTSPauseDisabled}
                        playerError={playerError}
                        videoItem={videoItem}
                        duration={videoDuration}
                        position={playerProgress}
                        volume={volume}
                        livePosition={livePosition}
                        readyToPlay={playerReady}
                        muted={muted}
                        isPaused={playerPaused}
                        isLive={isLive}
                        isStartover={isStartover}
                        isCatchup={isCatchup}
                        isVod={isVod}
                        isNpvr={isNpvr}
                        skipForwardDisabled={skipForwardDisabled}
                        skipBackwardDisabled={skipBackwardDisabled}
                        primaryColor={primary}
                        airTimeStart={videoItem?.startTime}
                        airTimeEnd={videoItem?.endTime}
                        seeking={seeking}
                        timeshiftModeOn={timeshiftModeOn}
                        timeshiftPosition={timeshiftPosition}
                        timeshiftDuration={timeshiftDuration}
                        jumpToLiveAllowed={jumpToLiveAllowed}
                        startoverAllowed={startoverAllowed && !errorExists}
                        startoverButtonVisible={
                            startoverButtonVisible && !errorExists
                        }
                        pauseAccessLevel={pauseAccessLevel}
                        rewindAccessLevel={rewindAccessLevel}
                        fastforwardAccessLevel={fastforwardAccessLevel}
                        playerTracks={playerTracks}
                        selectedAudio={selectedAudio}
                        selectedSubtitles={selectedSubtitles}
                        subtitlesDialogVisible={subtitlesDialogVisible}
                        subtitleButtonPosition={subtitleButtonPosition}
                        onSubtitlesButtonPress={onSubtitlesButtonPress}
                        onCloseSubtitlesDialog={onCloseSubtitlesDialog}
                        onSubtitlesModalClosed={onSubtitlesModalClosed}
                        onPlayPausePress={onPlayPausePress}
                        onSlidingStart={onSlidingStart}
                        onSkipForward={handleSkipForward}
                        onSkipBackward={handleSkipBackward}
                        onSlidingComplete={onSlidingCompleted}
                        onSlidingCompleteSilently={onSlidingCompleteSilently}
                        onSlidingValueChanged={onSlidingValueChanged}
                        onJumpToLive={handleJumpToLive}
                        onStartover={handleStartover}
                        onSelectAudio={onSelectAudio}
                        onSelectSubtitles={onSelectSubtitles}
                        orientation={orientation}
                        timeshiftIndicatorEnabled={timeshiftIndicatorEnabled}
                        onOpenDetails={handleOpenDetails}
                        onFullscreenPress={handleEnterWebFullscreen}
                        onMinimizePress={handleExitWebFullscreen}
                        onCloseModal={
                            isWeb ? handleCloseModalInWeb : handleCloseModal
                        }
                        onToggleMute={handleSetMuted}
                        recordingActionsDialogState={
                            recordingActionsDialogState
                        }
                        recordingItem={recordingItem}
                        zapping={zapping}
                        frameFitting={frameFitting}
                        onChangeVolume={handleChangeVolume}
                        playerWaiting={playerWaiting}
                        isBingeWatchingVisible={isBingeWatchingVisible}
                    />
                    <RecordingsNotificationsAndOverlay
                        isRecordingLoading={isRecordingLoading}
                        seriesId={seriesId}
                        handleCancelRecording={handleCancelRecording}
                        handleCancelSeriesRecording={
                            handleCancelSeriesRecording
                        }
                        recordingsNotification={recordingsNotification}
                        showQuotaDialog={showQuotaDialog}
                        showCancelEpisodeDialog={showCancelEpisodeDialog}
                        showCancelSeriesDialog={showCancelSeriesDialog}
                        showDeleteRecordingDialog={showDeleteRecordingDialog}
                        showDeleteSeriesDialog={showDeleteSeriesDialog}
                        resetNotificationCallback={resetNotificationCallback}
                        hideDeleteRecordingDialogCallback={
                            hideDeleteRecordingDialogCallback
                        }
                        hideDeleteSeriesDialogCallback={
                            hideDeleteSeriesDialogCallback
                        }
                        hideCancelEpisodeDialogCallback={
                            hideCancelEpisodeDialogCallback
                        }
                        hideCancelSeriesDialogCallback={
                            hideCancelSeriesDialogCallback
                        }
                        hideQuotaDialogCallback={hideQuotaDialogCallback}
                        pinDialogProps={pinDialogProps}
                        handleShowPinDialog={handleShowPinDialog}
                        playerInFullscreen={fullscreen}
                        recordingIsProtected={recordingIsProtected}
                        seriesHasProtectedRecording={
                            seriesHasProtectedRecording
                        }
                        onDeleteRecording={handleDeleteRecording}
                        onDeleteSeriesRecording={handleDeleteSeriesRecording}
                        onOpenDetails={handleOpenDetails}
                        fromPlayer
                    />
                    <Notification
                        notification={timeshiftNotification}
                        onResetNotification={onResetTimeshiftNotification}
                        fromPlayer
                    />
                    <Notification
                        notification={disabledNotification}
                        onResetNotification={onResetDisabledNotification}
                        fromPlayer
                    />
                </View>
                <ContentRatingOverlay
                    parentalRating={videoItem?.parentalRating}
                    visible={showContentRatingOverlay}
                />
                <GenericPinDialog
                    appState={appState}
                    visible={showPinDialog}
                    onClose={handleClosePinDialog}
                    onSuccess={onParentalControlChecked}
                    modal={false}
                />
                <ConnectionIndicatorAlert
                    handleClosePlayer={handleClosePlayer}
                    isPlayerNotification
                />
                {isBingeWatchingVisible && nextEpisode && (
                    <BingeWatchingOverlay
                        nextEpisode={nextEpisode}
                        countdown={bingewatchingCountdown}
                        onClose={onCloseBingeWatching}
                        onPlayNext={onPlayNextEpisode}
                        screenReaderEnabled={screenReaderEnabled}
                    />
                )}
                <NoInternetAlert isPlayerNotification />
            </GestureHandlerRootView>
        </View>
    );
}

export default PlayerModal;
